import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  CircularProgress,
  Paper,
  Alert,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import {
  Assignment as JobIcon,
  DirectionsCar as VehicleIcon,
  LocalGasStation as DepotIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const libraries = ['places'];
const googleMapsQueryUrl = (address) =>
  `https://maps.google.com/?q=${encodeURIComponent(address || '')}`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const downloadQuotePdf = async (quoteId) => {
  try {
    const response = await api.get(`/customers/quotes/${quoteId}/download_pdf/`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const short = String(quoteId).replace(/-/g, '').slice(0, 8);
    link.download = `quote-${short}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading quote PDF:', error);
    alert('Failed to download quote PDF. Please try again.');
  }
};

const CustomerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [stats, setStats] = useState({
    activeJobs: 0,
    completedJobs: 0,
    pendingInvoices: 0,
  });
  const [quotes, setQuotes] = useState([]);

  // Map-related constants (must be declared before useState that uses them)
  const defaultCenter = {
    lat: 47.6062, // Seattle, WA area (adjust to your service area)
    lng: -122.3321,
  };

  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 500px)',
    minHeight: '500px',
  };

  // Map-related state
  const [jobs, setJobs] = useState([]);
  const [fuelDepots, setFuelDepots] = useState([]);
  const [geocodedDepots, setGeocodedDepots] = useState({});
  const [mapLoading, setMapLoading] = useState(true); // Show loading until map is ready
  const [mapError, setMapError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const initialBoundsSetRef = useRef(false);
  const userHasChangedMapRef = useRef(false); // Track if user manually changed map
  const isUpdatingProgrammaticallyRef = useRef(false); // Track if we're updating programmatically
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);

  useEffect(() => {
    fetchData();
    fetchJobs();
    fetchDepots();
    fetchProfile();
    fetchQuotes();
    
    // Fallback timeout to hide loading if map doesn't load within 10 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      setMapLoading((prev) => {
        if (prev) {
          console.warn('Map loading timeout - hiding loading overlay');
          return false;
        }
        return prev;
      });
    }, 10000);
    
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && jobs.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeJobLocations(jobs);
    }
  }, [mapLoaded, jobs]);

  useEffect(() => {
    if (mapLoaded && fuelDepots.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeDepotLocations(fuelDepots);
    }
  }, [mapLoaded, fuelDepots]);

  const fetchData = async () => {
    try {
      const [jobsRes, invoicesRes] = await Promise.all([
        api.get('/customers/profile/jobs/'),
        api.get('/customers/profile/invoices/'),
      ]);

      const jobsData = Array.isArray(jobsRes.data) ? jobsRes.data : [];
      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];

      setStats({
        activeJobs: jobsData.filter((j) => ['pending', 'accepted', 'active'].includes(j.job_status)).length,
        completedJobs: jobsData.filter((j) => j.job_status === 'complete').length,
        pendingInvoices: invoices.filter((i) => i.status === 'submitted').length,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/customers/profile/profile/');
      setProfile(response.data);
      setFormData({
        company_name: response.data.company_name || '',
        phone_number: response.data.phone_number || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        notes: response.data.notes || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await api.get('/customers/profile/quotes/');
      const quotesData = Array.isArray(response.data) ? response.data : [];
      // Show only quotes that are not accepted (filter out accepted quotes)
      const nonAcceptedQuotes = quotesData.filter(quote => quote.status !== 'accepted');
      setQuotes(nonAcceptedQuotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    }
  };

  const handleProfileChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditProfile = () => {
    setEditingProfile(true);
    setProfileMessage('');
  };

  const handleCancelEdit = () => {
    setEditingProfile(false);
    // Reset form data to current profile values
    if (profile) {
      setFormData({
        company_name: profile.company_name || '',
        phone_number: profile.phone_number || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        notes: profile.notes || '',
      });
    }
    setProfileMessage('');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setProfileMessage('');

    try {
      // Use PUT if profile exists, POST if it doesn't
      const method = profile ? 'put' : 'post';
      await api[method]('/customers/profile/profile/', formData);
      setProfileMessage('Profile updated successfully');
      setEditingProfile(false);
      fetchProfile(); // Refresh profile data
      setTimeout(() => {
        setProfileMessage('');
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error saving profile. Please check your input.';
      setProfileMessage(errorMsg);
      console.error('Error saving profile:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setMapError('');
      // Don't set mapLoading here - it should be controlled by map load state
      const response = await api.get('/customers/profile/jobs/');
      const jobsData = Array.isArray(response.data) ? response.data : [];
      // Filter out completed jobs
      const activeJobs = jobsData.filter(job => job.job_status !== 'complete');
      setJobs(activeJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setMapError('Failed to load jobs');
    }
    // Note: mapLoading is controlled by map load events, not job fetching
  };

  const fetchDepots = async () => {
    try {
      const res = await api.get('/depots/').catch(() => ({ data: [] }));
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setFuelDepots(data);
    } catch (error) {
      console.error('Error fetching fuel depots:', error);
      setFuelDepots([]);
    }
  };

  const geocodeJobLocations = async (jobsData) => {
    if (!geocoderRef.current || !window.google) return;

    const geocoder = geocoderRef.current;
    const locations = {};

    for (const job of jobsData) {
      if (job.job_location && !geocodedLocations[job.id]) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: job.job_location }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(status);
              }
            });
          });

          locations[job.id] = {
            lat: result.lat(),
            lng: result.lng(),
          };
        } catch (err) {
          console.error(`Failed to geocode ${job.job_location}:`, err);
        }
      }
    }

    if (Object.keys(locations).length > 0) {
      setGeocodedLocations((prev) => ({ ...prev, ...locations }));
    }
  };

  const geocodeDepotLocations = async (depotsData) => {
    if (!geocoderRef.current || !window.google || !depotsData?.length) return;
    const geocoder = geocoderRef.current;
    const additions = {};
    for (const d of depotsData) {
      if (d.latitude != null && d.longitude != null) continue;
      const addr = d.full_address;
      if (!addr) continue;
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              reject(status);
            }
          });
        });
        additions[d.id] = {
          lat: result.lat(),
          lng: result.lng(),
        };
      } catch (err) {
        console.warn(`Failed to geocode depot ${addr}:`, err);
      }
    }
    if (Object.keys(additions).length > 0) {
      setGeocodedDepots((prev) => ({ ...prev, ...additions }));
    }
  };

  const getDepotPosition = (d) => {
    if (d.latitude != null && d.longitude != null) {
      return { lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) };
    }
    return geocodedDepots[d.id] || null;
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    setMapLoaded(true);
    setMapLoading(false); // Hide loading overlay when map is loaded
    
    // Clear the loading timeout since map loaded successfully
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  // Memoized callbacks to prevent infinite update loops
  // After initial setup, we don't update state to avoid re-renders
  const handleCenterChanged = useCallback(() => {
    // Ignore if we're updating programmatically
    if (isUpdatingProgrammaticallyRef.current) {
      return;
    }
    // Only track user changes, don't update state (prevents infinite loop)
    if (mapRef.current && initialBoundsSetRef.current) {
      userHasChangedMapRef.current = true;
    }
  }, []);

  const handleZoomChanged = useCallback(() => {
    // Ignore if we're updating programmatically
    if (isUpdatingProgrammaticallyRef.current) {
      return;
    }
    // Only track user changes, don't update state (prevents infinite loop)
    if (mapRef.current && initialBoundsSetRef.current) {
      userHasChangedMapRef.current = true;
    }
  }, []);

  const getMarkers = () => {
    const markers = [];

    // Add job markers
    jobs.forEach((job) => {
      if (geocodedLocations[job.id]) {
        markers.push({
          id: `job-${job.id}`,
          type: 'job',
          position: geocodedLocations[job.id],
          data: job,
          title: `Job ${job.id.substring(0, 8)} - ${job.job_location}`,
        });
      }
    });

    // Add driver/vehicle markers for jobs with assigned drivers
    jobs.forEach((job) => {
      if (job.vehicle_location && job.vehicle_location.latitude && job.vehicle_location.longitude) {
        markers.push({
          id: `driver-${job.id}`,
          type: 'driver',
          position: {
            lat: job.vehicle_location.latitude,
            lng: job.vehicle_location.longitude,
          },
          data: {
            ...job,
            vehicle_info: job.vehicle_info,
            driver_name: job.driver_name,
          },
          title: `${job.driver_name || 'Driver'} - ${job.vehicle_info || 'Vehicle'}`,
        });
      }
    });

    fuelDepots.forEach((d) => {
      const pos = getDepotPosition(d);
      if (pos) {
        markers.push({
          id: `depot-${d.id}`,
          type: 'depot',
          position: pos,
          data: d,
          title: d.name,
        });
      }
    });

    return markers;
  };

  const getMarkerIcon = (type) => {
    if (!window.google || !window.google.maps) return null;

    if (type === 'depot') {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#F9A825',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }

    if (type === 'driver') {
      // Vehicle icon
      return {
        path: 'M-20,-8 L-5,-8 L-5,-4 L5,-4 L5,-8 L20,-8 L20,8 L-20,8 Z M-15,-3 L-8,-3 L-8,1 L-15,1 Z M8,-3 L15,-3 L15,3 L8,3 Z',
        fillColor: '#1E3A5F',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1,
        anchor: new window.google.maps.Point(0, 0),
      };
    } else {
      // Job icon
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#4A90E2',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }
  };

  // Set initial map bounds/center only once when map first loads with data
  // Never change center/zoom if user has manually changed the map
  useEffect(() => {
    if (mapRef.current && mapLoaded && !initialBoundsSetRef.current && !userHasChangedMapRef.current) {
      const markers = getMarkers();
      
      // Set flag to prevent callbacks from firing during programmatic update
      isUpdatingProgrammaticallyRef.current = true;
      
      if (markers.length > 0) {
        // If we have markers, fit bounds to show all of them (only on initial load)
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((marker) => {
          bounds.extend(marker.position);
        });
        mapRef.current.fitBounds(bounds);
        initialBoundsSetRef.current = true;
      } else {
        // If no markers, use default center (already set)
        initialBoundsSetRef.current = true;
      }
      
      // Reset flag after a short delay to allow map to update
      setTimeout(() => {
        isUpdatingProgrammaticallyRef.current = false;
      }, 100);
    }
  }, [mapLoaded, geocodedLocations, geocodedDepots, jobs, fuelDepots]);

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Customer Dashboard
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Company Details Panel - At the top */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Company Details"
                subheader="Your company information"
                action={
                  !editingProfile && profile && (
                    <IconButton onClick={handleEditProfile} color="primary">
                      <EditIcon />
                    </IconButton>
                  )
                }
              />
              <CardContent>
                {profileMessage && (
                  <Alert 
                    severity={profileMessage.includes('success') ? 'success' : 'error'} 
                    sx={{ mb: 2 }}
                    onClose={() => setProfileMessage('')}
                  >
                    {profileMessage}
                  </Alert>
                )}
                {profile ? (
                  editingProfile ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Company Name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleProfileChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={profile.email || 'N/A'}
                          disabled
                          helperText="Email cannot be changed"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone_number"
                          value={formData.phone_number}
                          onChange={handleProfileChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Street Address"
                          name="address"
                          value={formData.address}
                          onChange={handleProfileChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="City"
                          name="city"
                          value={formData.city}
                          onChange={handleProfileChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>State</InputLabel>
                          <Select
                            name="state"
                            value={formData.state}
                            onChange={handleProfileChange}
                            label="State"
                          >
                            <MenuItem value="">Select State</MenuItem>
                            {US_STATES.map((state) => (
                              <MenuItem key={state} value={state}>
                                {state}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Zip Code"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleProfileChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleProfileChange}
                          multiline
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveProfile}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Company Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.company_name || 'Not set'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Email</Typography>
                        <Typography variant="body1">{profile.email || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                        <Typography variant="body1">{profile.phone_number || 'Not set'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Street Address</Typography>
                        <Typography variant="body1">{profile.address || 'Not set'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">City</Typography>
                        <Typography variant="body1">{profile.city || 'Not set'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">State</Typography>
                        <Typography variant="body1">{profile.state || 'Not set'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">Zip Code</Typography>
                        <Typography variant="body1">{profile.zip_code || 'Not set'}</Typography>
                      </Grid>
                      {profile.notes && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Notes</Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{profile.notes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  )
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Company details not available. Please complete your profile.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Jobs
                </Typography>
                <Typography variant="h4">{stats.activeJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed Jobs
                </Typography>
                <Typography variant="h4">{stats.completedJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Invoices
                </Typography>
                <Typography variant="h4">{stats.pendingInvoices}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quotes Panel */}
          {quotes.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Quotes"
                  subheader="View and manage your quotes"
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {quotes.map((quote) => (
                      <Paper key={quote.id} elevation={1} sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="textSecondary">
                              Quote ID: {quote.id.substring(0, 8)}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {parseFloat(quote.diesel_quantity || 0).toFixed(2)} gal Diesel
                              {parseFloat(quote.def_quantity || 0) > 0 && ` / ${parseFloat(quote.def_quantity || 0).toFixed(2)} gal DEF`}
                              {' - '}{quote.frequency}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              Delivery: {quote.delivery_address}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="h6" color="primary">
                              ${quote.estimated_price}
                            </Typography>
                            {quote.delivery_fee > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Delivery Fee: ${parseFloat(quote.delivery_fee || 0).toFixed(2)}
                              </Typography>
                            )}
                            <Chip
                              label={quote.status}
                              color={
                                quote.status === 'accepted' ? 'success' :
                                quote.status === 'submitted' ? 'info' :
                                quote.status === 'rejected' ? 'error' :
                                quote.status === 'expired' ? 'warning' : 'default'
                              }
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadQuotePdf(quote.id)}
                              >
                                Download PDF
                              </Button>
                              {quote.status === 'submitted' && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  fullWidth
                                  onClick={async () => {
                                    try {
                                      await api.post(`/customers/quotes/${quote.id}/accept/`);
                                      fetchQuotes();
                                      fetchData();
                                      alert('Quote accepted! A new order has been created.');
                                    } catch (error) {
                                      console.error('Error accepting quote:', error);
                                      alert('Error accepting quote. Please try again.');
                                    }
                                  }}
                                >
                                  Accept Quote
                                </Button>
                              )}
                              {quote.status === 'draft' && (
                                <Typography variant="body2" color="textSecondary">
                                  Draft quote
                                </Typography>
                              )}
                              {quote.status === 'accepted' && (
                                <Typography variant="body2" color="success.main">
                                  Quote accepted
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Map View Panel */}
          <Grid item xs={12} sx={{ mt: 2 }}>
          <Card>
            <CardHeader
              title="Map View - Jobs, Drivers & Fuel Depots"
              subheader="View your active jobs, assigned drivers, and company fuel depots on the map"
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip
                  icon={<JobIcon />}
                  label={`${jobs.length} Active Jobs`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<VehicleIcon />}
                  label={`${jobs.filter(job => job.driver_name).length} Assigned Drivers`}
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  icon={<DepotIcon />}
                  label={`${fuelDepots.length} Fuel Depots`}
                  color="warning"
                  variant="outlined"
                />
              </Box>

              {mapError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMapError('')}>
                  {mapError}
                </Alert>
              )}

              {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && !'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E' ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
                </Alert>
              ) : null}

              {scriptError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled.
                </Alert>
              ) : null}

              <Paper elevation={2} sx={{ overflow: 'hidden', position: 'relative', mt: 2 }}>
            {mapLoading && !scriptError && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading map...
                </Typography>
              </Box>
            )}
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E'}
              libraries={libraries}
              onLoad={() => {
                setScriptLoaded(true);
                setMapLoading(false); // Hide loading overlay when script is loaded
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                }
              }}
              onError={() => {
                setScriptError(true);
                setMapLoading(false); // Hide loading overlay on script error
                setMapError('Failed to load Google Maps script. Please check your API key.');
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                }
              }}
            >
              {scriptError ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" p={3}>
                  <Alert severity="error">
                    Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled in Google Cloud Console.
                  </Alert>
                </Box>
              ) : scriptLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={!initialBoundsSetRef.current ? mapCenter : undefined}
                  zoom={!initialBoundsSetRef.current ? mapZoom : undefined}
                  onCenterChanged={handleCenterChanged}
                  onZoomChanged={handleZoomChanged}
                  onLoad={onMapLoad}
                  onError={() => {
                    setMapLoading(false);
                    setMapError('Failed to load Google Map. Please try refreshing the page.');
                    if (loadingTimeoutRef.current) {
                      clearTimeout(loadingTimeoutRef.current);
                    }
                  }}
                  options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                  }}
                >
                  {getMarkers().map((marker) => (
                    <Marker
                      key={marker.id}
                      position={marker.position}
                      title={marker.title}
                      icon={getMarkerIcon(marker.type)}
                      onClick={() => setSelectedMarker(marker)}
                    />
                  ))}

                  {selectedMarker && (
                    <InfoWindow
                      position={selectedMarker.position}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <Box sx={{ p: 1, maxWidth: 250 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {selectedMarker.type === 'driver' ? (
                            <>
                              <VehicleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                              Driver
                            </>
                          ) : selectedMarker.type === 'depot' ? (
                            <>
                              <DepotIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                              Fuel depot
                            </>
                          ) : (
                            <>
                              <JobIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                              Job
                            </>
                          )}
                        </Typography>
                        {selectedMarker.type === 'driver' ? (
                          <>
                            <Typography variant="body2">
                              <strong>Driver:</strong> {selectedMarker.data.driver_name || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Vehicle:</strong> {selectedMarker.data.vehicle_info || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Job Location:</strong> {selectedMarker.data.job_location}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong> {selectedMarker.data.job_status}
                            </Typography>
                          </>
                        ) : selectedMarker.type === 'depot' ? (
                          <>
                            <Typography variant="body2">
                              <strong>{selectedMarker.data.name}</strong>
                            </Typography>
                            <Typography variant="body2">
                              <strong>Operator:</strong> {selectedMarker.data.parent_company}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <a
                                href={googleMapsQueryUrl(selectedMarker.data.full_address)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {selectedMarker.data.full_address}
                              </a>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Fuels:</strong>{' '}
                              {[selectedMarker.data.has_diesel && 'Diesel', selectedMarker.data.has_def && 'DEF']
                                .filter(Boolean)
                                .join(', ')}
                            </Typography>
                            {(selectedMarker.data.contact_phone || selectedMarker.data.contact_email) && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                <strong>Contact:</strong>{' '}
                                {[selectedMarker.data.contact_name, selectedMarker.data.contact_phone, selectedMarker.data.contact_email]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>Job ID:</strong> {selectedMarker.data.id.substring(0, 8)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Location:</strong>{' '}
                              <a
                                href={googleMapsQueryUrl(selectedMarker.data.job_location)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {selectedMarker.data.job_location}
                              </a>
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong> {selectedMarker.data.job_status}
                            </Typography>
                            {selectedMarker.data.driver_name && (
                              <Typography variant="body2">
                                <strong>Driver:</strong> {selectedMarker.data.driver_name}
                              </Typography>
                            )}
                            {selectedMarker.data.vehicle_info && (
                              <Typography variant="body2">
                                <strong>Vehicle:</strong> {selectedMarker.data.vehicle_info}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              <strong>Diesel:</strong> {parseFloat(selectedMarker.data.diesel_quantity || 0).toFixed(2)} gal
                            </Typography>
                            <Typography variant="body2">
                              <strong>DEF:</strong> {parseFloat(selectedMarker.data.def_quantity || 0).toFixed(2)} gal
                            </Typography>
                          </>
                        )}
                      </Box>
                    </InfoWindow>
                  )}
                </GoogleMap>
              ) : null}
            </LoadScript>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default CustomerDashboard;



